<template>
  <b-modal v-model="showModalLocal" size="lg" centered title="Eliminar Producto" @hidden="closeModal"
    header-class="border-0" content-class="p-4" hide-footer body-class="pt-0">
    <div class="modal-body text-center">
      <!-- Warning Icon -->
      <div class="warning-icon mb-4">
        <i class="fa fa-exclamation-circle"></i>
      </div>

      <!-- Product Info -->
      <div class="product-info delete-product mb-2">
        <div class="product-label">
          <i class="fa fa-box-open me-2"></i>
          Producto a Eliminar
        </div>
        <div class="mt-2">
          FABRICANTE: {{ getFabricanteName }}
        </div>
        <h3 class="product-name mt-2">{{ product.nombre }}</h3>
        <div class="product-code">ID: {{ product.id }}</div>
      </div>

      <!-- Selected Product Info (if exists) -->
      <div v-if="selectedProduct" class="position-relative">
        <div class="arrow-connector">
          <i class="fa fa-arrow-down"></i>
        </div>
        <div class="product-info selected-product">
          <div class="product-label">
            <i class="fa fa-exchange-alt me-2"></i>
            Producto a Asociar
          </div>
          <div class="mt-2">
            FABRICANTE: {{ getSelectedProductFabricante }}
          </div>
          <h3 class="product-name mt-2">{{ getSelectedProductName }}</h3>
          <div class="product-code">ID: {{ selectedProduct }}</div>
        </div>
      </div>

      <h4 class="confirmation-text mb-2">¿Estás seguro de eliminar este producto?</h4>
      <p class="text-muted mb-4">Esta acción no se puede deshacer</p>

      <!-- Association Option -->
      <div class="association-section p-3">
        <b-form-checkbox v-model="enableAssociation" class="mb-3" switch @change="handleSwitchChange">
          <span class="association-text">Asociar a otro producto</span>
        </b-form-checkbox>

        <div v-if="enableAssociation" class="mt-3 animated fadeIn">
          <div class="form-group">
            <label class="select-label mb-2">Seleccionar producto para asociar:</label>
            <el-select v-model="selectedProduct" filterable remote :loading="loading" reserve-keyword
              placeholder="Escribe nombre o ID para buscar productos..." :remote-method="handleSearch" :loading-text="loadingText"
              :no-data-text="noDataText" :no-match-text="noMatchText" class="w-100"
              popper-class="product-select-dropdown" clearable>
              <el-option v-for="item in limitedProductOptions" :key="item.value" :label="item.text" :value="item.value">
                <div class="product-option">{{ item.id }} - {{ item.text }}</div>
              </el-option>
            </el-select>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Buttons -->
    <div class="modal-footer border-0 pt-0">
      <div class="d-flex justify-content-center w-100">
        <b-button variant="danger" @click="closeModal" class="btn-cancel mr-2">
          Cancelar
        </b-button>
        <b-button variant="primary" @click="confirmDelete" :disabled="enableAssociation && !selectedProduct"
          class="btn-delete">
          Eliminar
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  name: 'ModalDeleteProduct',
  props: {
    product: {
      type: Object,
      required: true
    },
    show_modal: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data() {
    return {
      enableAssociation: false,
      selectedProduct: null,
      productOptions: [],
      showModalLocal: false,
      loading: false,
      loadingText: 'Cargando...',
      noDataText: 'No hay resultados',
      noMatchText: 'No hay coincidencias',
      searchQuery: ''
    };
  },

  created() {
    this.debouncedSearch = debounce(this.fetchProducts, 800);
  },

  computed: {
    ...mapState('productos', ['activeDistinctProducts']),

    limitedProductOptions() {
      return this.productOptions.slice(0, 50);
    },

    getFabricanteName() {
      return this.product.fabricante && this.product.fabricante.nombre
        ? this.product.fabricante.nombre
        : 'Sin fabricante';
    },

    getSelectedProductName() {
      const selectedOption = this.productOptions.find(opt => opt.value === this.selectedProduct);
      return selectedOption ? selectedOption.text.split(' - ')[1] : '';
    },

    getSelectedProductFabricante() {
      const selectedOption = this.productOptions.find(opt => opt.value === this.selectedProduct);
      return selectedOption ? selectedOption.text.split(' - ')[0] : 'Sin fabricante';
    }
  },

  watch: {
    show_modal: {
      immediate: true,
      handler(newVal) {
        this.showModalLocal = newVal;
      }
    },
    enableAssociation(newVal) {
      if (!newVal) {
        this.selectedProduct = null;
        this.productOptions = [];
      }
    },
    activeDistinctProducts: {
      handler(products) {
        if (products && Array.isArray(products)) {
          this.productOptions = products.map(product => ({
            value: product.id,
            id: product.id,
            text: product.fabricante ? `${product.fabricante.nombre} - ${product.nombre}` : product.nombre
          }));
        }
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('productos', [
      'getActiveDistinctProducts',
      'deleteProductWithAssociation',
      'deleteProduct',
      'getProductos'
    ]),

    async handleSwitchChange(value) {
      if (value) {
        this.loading = true;
        await this.fetchProducts('');
        this.loading = false;
      }
    },

    async handleSearch(query) {
      if (!query || query.length < 2) {
        await this.fetchProducts('');
        return;
      }

      this.searchQuery = query;
      this.loading = true;
      await this.debouncedSearch(query);
      this.loading = false;
    },

    async fetchProducts(query) {
      try {
        await this.getActiveDistinctProducts({
          product_id: this.product.id,
          search: query
        });
      } catch (error) {
        console.error('Error fetching products:', error);
        this.productOptions = [];
      }
    },

    async confirmDelete() {
      try {
        if (this.enableAssociation && this.selectedProduct) {
          await this.deleteProductWithAssociation({
            productId: this.product.id,
            associateWithId: this.selectedProduct
          });
          await this.getProductos();
        } else {
          await this.deleteProduct(this.product);
          await this.getProductos();
        }
        this.closeModal();
      } catch (error) {
        this.$bvToast.toast('Error al eliminar el producto', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    closeModal() {
      this.enableAssociation = false;
      this.selectedProduct = null;
      this.productOptions = [];
      this.showModalLocal = false;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.modal-body {
  padding: 1.5rem 1rem;
}

.warning-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto;
  border-radius: 50%;
  background: #fff5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #ff4d4f;
}

.warning-icon i {
  font-size: 30px;
  color: #ff4d4f;
}

.product-info {
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  text-align: left;
  position: relative;
  transition: all 0.3s ease;
}

.product-info.delete-product {
  background: #fff1f2;
  border: 1px solid #fecdd3;
}

.product-info.selected-product {
  background: #f0f9ff;
  border: 1px solid #e0f2fe;
}

.product-label {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.arrow-connector {
  position: absolute;
  top: -1.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  font-size: 1rem;
}

.product-name {
  font-size: 1.25rem;
  color: #1f2937;
  margin: 0.5rem 0;
  font-weight: 600;
}

.product-code {
  color: #6b7280;
  font-size: 0.875rem;
}

.confirmation-text {
  font-size: 1.125rem;
  color: #1f2937;
  font-weight: 600;
}

.association-section {
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 1rem;
}

.association-text {
  font-weight: 500;
  color: #374151;
}

.select-label {
  color: #374151;
  font-weight: 500;
  text-align: left;
}

.btn-cancel,
.btn-delete {
  min-width: 120px;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  border-radius: 6px;
}

.btn-cancel {
  background: #f64040;
  border: none;
  color: white;
}

.btn-cancel:hover {
  background: #cc2e2e;
}

.btn-delete {
  background: #0fbef9;
  border: none;
}

.btn-delete:hover:not(:disabled) {
  background: #029ed2;
}

.btn-delete:disabled {
  background: rgb(172, 208, 249);
}

:deep(.el-select-dropdown) {
  max-height: 300px !important;
  border-radius: 8px;
}

:deep(.el-input__inner) {
  height: 40px;
  border-radius: 6px;
  border-color: #d1d5db;
}

:deep(.el-select-dropdown__item) {
  padding: 0.75rem 1rem;
}

.product-option {
  font-size: 0.9rem;
  color: #374151;
}

.animated {
  animation-duration: 0.3s;
  animation-fill-mode: both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fadeIn {
  animation-name: fadeIn;
}
</style>